import { ReactNode, useEffect } from 'react';
import {
    AdSenseForShoppingPageOptions,
    AdSenseForShoppingAdBlock,
    Vertical,
    DeviceType,
} from '../../shared/types/ast';

import {
    bannerName,
    RESULT_LIST_ALT_SPACE_3,
    RESULT_LIST_ALT_SPACE_4,
    RESULT_LIST_SPACE_2,
    RESULT_LIST_SPACE_3,
    RESULT_LIST_SPACE_4,
    RESULT_LIST_SPACE_5,
} from '../utils/result';
import { SearchSummary } from '../../shared/types/adsense';
import { getFeatureStatus } from '../config';
import { displayAdsLog } from '../utils/debug';

export const SEARCH_RESULT_PAGES = 'partner-vert-pla-scm-finn-no-srp';
export const CATEGORY_PAGES = 'partner-vert-pla-scm-finn-no-cat';
export const PROD_DETAIL_PAGE = 'partner-vert-pla-scm-finn-no-pdp';
export const GRID_VIEW_STYLE_ID = 6085245841;
export const LIST_VIEW_STYLE_ID = 2950930037;
export const OBJECT_VIEW_STYLE_ID = 4934454942;
export const ZERO_RESULT_CHANNEL = 'zsrp';

type ASFHType = 'object' | 'recirc-object';

const defaultAsfhPageOpts = {
    hl: 'no',
    adsafe: 'high',
    // We support click tracking even if we don't open links in new windows
    linkTarget: '_blank',
};

function createPageOption(
    page: number,
    query: string,
    fallback: string,
    channel: string,
    styleId: number,
    zeroResults?: boolean,
): AdSenseForShoppingPageOptions {
    const combinedQuery = query || fallback;

    return {
        pubId: query ? SEARCH_RESULT_PAGES : CATEGORY_PAGES,
        query: combinedQuery,
        channel: zeroResults ? ZERO_RESULT_CHANNEL : channel,
        adPage: page,
        styleId,
        ...defaultAsfhPageOpts,
    };
}

function createAdBlock(targetId: string) {
    const afshBlock: AdSenseForShoppingAdBlock = {
        container: targetId,
    };
    const element = document.getElementById(targetId);
    if (element) {
        const previousElement = element.previousElementSibling;

        if (previousElement && previousElement.clientHeight > 0) {
            afshBlock.height = previousElement.clientHeight;
            afshBlock.width = previousElement.clientWidth;
        }
    } else {
        console.error(
            `Unable to find DOM element ${targetId} during setup of AdSenseForShopping`,
        );
    }

    return afshBlock;
}

function clearAdBlock(targetId: string) {
    const element = document.getElementById(targetId);
    if (element) {
        element.innerHTML = '';
    }
}

export function injectASFHToContainer(
    containerId: string,
    query: string,
    channel: string,
    type: ASFHType,
    width?: number,
    height?: number,
) {
    if (!containerId || !query) return;
    try {
        if (typeof window._googCsa === 'function') {
            const pageOptions: AdSenseForShoppingPageOptions = {
                pubId: PROD_DETAIL_PAGE,
                styleId: OBJECT_VIEW_STYLE_ID,
                adPage: 1,
                query,
                channel,
                ...defaultAsfhPageOpts,
            };

            const adBlock: AdSenseForShoppingAdBlock = {
                container: containerId,
                width: width || 310,
                number: 2,
                adLoadedCallback: (containerName) => {
                    displayAdsLog(
                        `Google ASFH adLoadedCallback ${containerName}`,
                    );
                },
                noAdLoadedCallback: (containerName) => {
                    displayAdsLog(
                        `Google ASFH noAdLoadedCallback ${containerName}`,
                    );
                },
            };

            if (type === 'recirc-object') {
                pageOptions.styleId = GRID_VIEW_STYLE_ID;
                delete pageOptions.adPage;

                adBlock.height = height;
                delete adBlock.number;
            }

            window._googCsa('plas', pageOptions, adBlock);
        }
    } catch (error) {
        console.error('Error in setup of AdSenseForShopping', error);
    }
}

export function useAdSenseObjectPage(
    containerId: string,
    query: string,
    channel: string,
    type: ASFHType,
    width?: number,
    height?: number,
) {
    useEffect(() => {
        if (containerId && query) {
            injectASFHToContainer(
                containerId,
                query,
                channel,
                type,
                width,
                height,
            );
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [containerId, query]);
}

/**
 * Used for Search Results
 * @param children
 * @param deviceType
 * @param vertical
 * @param config
 * @param grid
 * @param zeroResults
 */
export function useAdSenseSearchResult(
    children: ReactNode[],
    deviceType: DeviceType,
    vertical?: Vertical,
    config?: SearchSummary,
    grid?: boolean,
    zeroResults?: boolean,
) {
    const altSpacingToggleEnabled = getFeatureStatus('enableAdIntervalVariant');
    const enableAdIntervalVariant =
        altSpacingToggleEnabled && deviceType === 'mobile';

    const showAdSense = vertical?.toLowerCase() === 'bap';
    const showAdSenseBoards = showAdSense && !!config?.query;

    let adjustedSpace3 = enableAdIntervalVariant
        ? RESULT_LIST_ALT_SPACE_3
        : RESULT_LIST_SPACE_3;
    let adjustedSpace4 = enableAdIntervalVariant
        ? RESULT_LIST_ALT_SPACE_4
        : RESULT_LIST_SPACE_4;
    let adjustedSpace5 = RESULT_LIST_SPACE_5;

    const positions: string[] = [];

    if (showAdSenseBoards && children.length > RESULT_LIST_SPACE_2) {
        positions.push(bannerName(deviceType, 2));
    }

    if (showAdSenseBoards && children.length > adjustedSpace3) {
        positions.push(bannerName(deviceType, 3));
    }

    if (showAdSenseBoards && children.length > adjustedSpace4) {
        positions.push(bannerName(deviceType, 4));
    }

    if (showAdSenseBoards && children.length > adjustedSpace5) {
        positions.push(bannerName(deviceType, 5));
    }

    positions.push('afshcontainer');

    const { channel = '', fallback = '', page = 1, query = '' } = config || {};
    const positionsCheck = positions.join();

    useEffect(() => {
        if (showAdSense) {
            try {
                if (typeof window._googCsa === 'function') {
                    const styleId = grid
                        ? GRID_VIEW_STYLE_ID
                        : LIST_VIEW_STYLE_ID;
                    const pageOptions = createPageOption(
                        page,
                        query,
                        fallback,
                        channel,
                        styleId,
                        zeroResults,
                    );
                    const adBlockPositions = positions.map(createAdBlock);
                    if (pageOptions.query && adBlockPositions.length) {
                        window._googCsa('plas', pageOptions, adBlockPositions);
                    }
                }
            } catch (error) {
                console.error('Error in setup of AdSenseForShopping', error);
            }
        }
        return function cleanup() {
            if (showAdSense) {
                positions.forEach(clearAdBlock);
            }
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [
        positionsCheck, // if we use the positions array her it will update to often
        showAdSense,
        page,
        query,
        fallback,
        channel,
        grid,
        zeroResults,
    ]);

    return {
        showAdSense,
        showAdSenseBoards,
    };
}
